import type { LucideIcon } from 'lucide-react'
import clsx from 'clsx'
import type { Trend } from '@/types'

interface StatCardProps {
  title: string
  value: string
  subtitle?: string
  icon: LucideIcon
  trend?: Trend
  trendLabel?: string
  /** Colorizes the value text */
  valueColor?: 'default' | 'success' | 'danger' | 'warning'
  className?: string
}

const trendConfig = {
  up:      { arrow: '↑', color: 'text-brand-400' },
  down:    { arrow: '↓', color: 'text-red-400'   },
  neutral: { arrow: '→', color: 'text-slate-400'  },
}

const valueColorStyles: Record<string, string> = {
  default: 'text-slate-100',
  success: 'text-brand-400',
  danger:  'text-red-400',
  warning: 'text-amber-400',
}

/**
 * Stat card for dashboard metrics.
 * Shows metric value with icon, optional trend, and subtitle.
 * Always shows a label context — never raw numbers without explanation.
 */
export function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendLabel,
  valueColor = 'default',
  className,
}: StatCardProps) {
  return (
    <div className={clsx('stat-card', className)}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            {title}
          </p>
          <p className={clsx('text-2xl font-bold tracking-tight mt-0.5', valueColorStyles[valueColor])}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
          )}
          {trend && trendLabel && (
            <div className="flex items-center gap-1 mt-2">
              <span className={clsx('text-xs font-medium', trendConfig[trend].color)}>
                {trendConfig[trend].arrow} {trendLabel}
              </span>
            </div>
          )}
        </div>
        <div className="shrink-0 ml-3">
          <div className="w-10 h-10 rounded-lg bg-slate-700/50 border border-slate-600/50 flex items-center justify-center">
            <Icon size={18} className="text-slate-400" />
          </div>
        </div>
      </div>
    </div>
  )
}
